import { Divider, Paper, Typography } from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import { ExtraSmallButton } from '../../custom'
import { useSelector } from 'react-redux'
import { selectBatchId, selectTradeOrders } from '../../../selectors/strategyBuilder'
import { useDispatch } from 'react-redux'
import { TradeOrder, updateTradeOrders } from '../../../reducer/strategyBuilder.reducer'
import ThinArrowLeftIcon from '../../../assets/icons/ThinArrowLeftIcon'
import ThinArrowRightIcon from '../../../assets/icons/ThinArrowRightIcon'
import { calculateGcd } from '../../../util/orders'
import { selectOrdersHistory } from '../../../selectors/orders'
import { Order, OrderStatus } from '../../../proto/generated/order'

const QuantityControls: React.FC<{disabled?: boolean}> = ({disabled}) => {
    const dispatch = useDispatch()
    const tradeOrders = useSelector(selectTradeOrders)
    const batchId = useSelector(selectBatchId);
    const orderHistory = useSelector(selectOrdersHistory);

    const orderHistoryMap = useMemo(() => {
        const map: Record<string, Order> = {};
        return orderHistory.reduce((acc, order) => {
            acc[order.orderId] = order;
            return acc;
        }, map);
    }, [orderHistory]);

    const eligibleTradeOrders = useMemo(() => {
        return tradeOrders.filter(order => !orderHistoryMap[order.referenceOrderId] || orderHistoryMap[order.referenceOrderId].batchId === batchId );
    }, [tradeOrders, orderHistoryMap, batchId]);

    const [decreaseEnabled, increaseEnabled] = useMemo(() => {
        if (eligibleTradeOrders.length === 0) {
            return [false, false]
        }
        const quantities = eligibleTradeOrders.map((t) => t.quantity)
        const lotSizes = eligibleTradeOrders.map((t) => t.security.lotSize ?? 1)
        const quantitiesInLots = quantities.map((qty, index) => qty / lotSizes[index])
        const gcdValue = calculateGcd(quantitiesInLots)
        const ratios = quantitiesInLots.map((qty) => qty / gcdValue)

        const canDecrease = eligibleTradeOrders.every((t, index) => {
            const lotSize = t.security.lotSize ?? 1
            const ratio = ratios[index]
            const newQuantity = t.quantity - ratio * lotSize
            return newQuantity >= lotSize
        })

        const canIncrease = eligibleTradeOrders.every((t, index) => {
            const lotSize = t.security.lotSize ?? 1
            const ratio = ratios[index]
            const newQuantity = t.quantity + ratio * lotSize
            return newQuantity <= t.security.volumeFreezeQuantity
        })

        return [canDecrease, canIncrease]
    }, [eligibleTradeOrders])

    const onQuantityChange = useCallback(
        async (direction: number) => {
            if (eligibleTradeOrders.length === 0) return
            const quantities = eligibleTradeOrders.map((t) => t.quantity)
            const lotSizes = eligibleTradeOrders.map((t) => t.security.lotSize ?? 1)
            const quantitiesInLots = quantities.map((qty, index) => qty / lotSizes[index])

            const gcdValue = calculateGcd(quantitiesInLots)
            const ratios = quantitiesInLots.map((qty) => qty / gcdValue)

            const newTradeOrders: TradeOrder[] = tradeOrders.map((t, index) => {
                if (t.referenceOrderId && orderHistoryMap[t.referenceOrderId]?.status === OrderStatus.EXECUTED) return t;
                
                const lotSize = t.security.lotSize ?? 1
                const ratio = ratios[index]
                const currentQty = t.quantity
                const changeAmount = direction * ratio * lotSize
                const newQuantity = currentQty + changeAmount

                if (newQuantity < lotSize || newQuantity > t.security.volumeFreezeQuantity) {
                    return t
                } else {
                    return {
                        ...t,
                        quantity: newQuantity
                    }
                }
            })

            const hasChanges = newTradeOrders.some((newOrder, index) => newOrder.quantity !== tradeOrders[index].quantity)

            if (hasChanges) {
                dispatch(updateTradeOrders(newTradeOrders))
            }
        },
        [tradeOrders, eligibleTradeOrders, orderHistoryMap]
    )

    const onQuantityIncrease = useCallback(() => {
        onQuantityChange(1)
    }, [onQuantityChange])

    const onQuantityDecrease = useCallback(() => {
        onQuantityChange(-1)
    }, [onQuantityChange])

    return (
        <>
            <Typography variant="caption" color={'text.secondary'} style={{ margin: '5px 0' }}>
                QUANTITY
            </Typography>
            <Paper className="relative flex items-center !rounded-sm" sx={{ background: (theme) => theme.palette.background.default }}>
                <ExtraSmallButton
                    data-event={'Strike Down Clicked'}
                    data-event-source={'Trade'}
                    style={{ width: '50px', height: '100%' }}
                    onClick={onQuantityDecrease}
                    disabled={!decreaseEnabled || disabled}
                    className="button-icons"
                    color={'inherit'}
                    size={'small'}
                >
                    <ThinArrowLeftIcon />
                </ExtraSmallButton>
                <Divider orientation="vertical" variant="middle" flexItem />
                <ExtraSmallButton
                    data-event={'Strike Up Clicked'}
                    data-event-source={'Trade'}
                    style={{ width: '50px', height: '100%' }}
                    onClick={onQuantityIncrease}
                    disabled={!increaseEnabled || disabled}
                    className="button-icons"
                    color={'inherit'}
                    size={'small'}
                >
                    <ThinArrowRightIcon />
                </ExtraSmallButton>
            </Paper>
        </>
    )
}

export default memo(QuantityControls)
